import { useRef, useState, type KeyboardEvent } from 'react';
import { Bold, Code, Italic, Link, List } from 'lucide-react';

import { cn } from '../../lib/cn';
import { isSubmitHotkey, insertCodeBlock, insertLink, insertList, wrapSelection } from '../../lib/markdown';
import { IconButton } from '../IconButton';
import { MarkdownView } from '../MarkdownView';
import styles from './MarkdownEditor.module.css';

export type MarkdownEditorProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** `Cmd/Ctrl+Enter` — отправить. */
  readonly onSubmit?: () => void;
  readonly onCancel?: () => void;
  readonly placeholder?: string;
  readonly minRows?: number;
  readonly autoFocus?: boolean;
  readonly submitLabel?: string;
  readonly isSubmitting?: boolean;
  readonly ariaLabel?: string;
  readonly footer?: React.ReactNode;
};

/** Textarea + вкладка превью и тулбар (docs/06 §3.2, §4). */
export function MarkdownEditor({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder = 'Написать…',
  minRows = 4,
  autoFocus = false,
  submitLabel,
  isSubmitting = false,
  ariaLabel = 'Редактор markdown',
  footer,
}: MarkdownEditorProps) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const applyEdit = (edit: { value: string; caret: number }): void => {
    onChange(edit.value);
    window.requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (node) {
        node.focus();
        node.setSelectionRange(edit.caret, edit.caret);
      }
    });
  };

  const currentSelection = (): { value: string; start: number; end: number } => {
    const node = textareaRef.current;
    return {
      value,
      start: node?.selectionStart ?? value.length,
      end: node?.selectionEnd ?? value.length,
    };
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (isSubmitHotkey(event) && onSubmit) {
      event.preventDefault();
      onSubmit();
      return;
    }
    if (event.key === 'Escape' && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className={styles['editor']}>
      <div className={styles['header']}>
        <div className={styles['tabs']} role="tablist" aria-label="Режим редактора">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'write'}
            className={cn(styles['tab'], tab === 'write' && styles['tabActive'])}
            onClick={() => setTab('write')}
          >
            Написать
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'preview'}
            className={cn(styles['tab'], tab === 'preview' && styles['tabActive'])}
            onClick={() => setTab('preview')}
          >
            Превью
          </button>
        </div>
        {tab === 'write' ? (
          <div className={styles['toolbar']}>
            <IconButton
              size="sm"
              label="Жирный"
              tooltip="Жирный"
              icon={<Bold size={14} />}
              onClick={() => applyEdit(wrapSelection(currentSelection(), '**'))}
            />
            <IconButton
              size="sm"
              label="Курсив"
              tooltip="Курсив"
              icon={<Italic size={14} />}
              onClick={() => applyEdit(wrapSelection(currentSelection(), '_'))}
            />
            <IconButton
              size="sm"
              label="Список"
              tooltip="Список"
              icon={<List size={14} />}
              onClick={() => applyEdit(insertList(currentSelection()))}
            />
            <IconButton
              size="sm"
              label="Код"
              tooltip="Блок кода"
              icon={<Code size={14} />}
              onClick={() => applyEdit(insertCodeBlock(currentSelection()))}
            />
            <IconButton
              size="sm"
              label="Ссылка"
              tooltip="Ссылка"
              icon={<Link size={14} />}
              onClick={() => applyEdit(insertLink(currentSelection()))}
            />
          </div>
        ) : null}
      </div>

      {tab === 'write' ? (
        <textarea
          ref={textareaRef}
          autoFocus={autoFocus}
          value={value}
          rows={minRows}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className={styles['textarea']}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <div className={styles['preview']}>
          <MarkdownView value={value} />
        </div>
      )}

      <div className={styles['footer']}>
        <span className={styles['hint']}>Cmd+Enter — {submitLabel ?? 'отправить'}</span>
        <div className={styles['footerActions']}>
          {footer}
          {onCancel ? (
            <button type="button" className={styles['cancel']} onClick={onCancel} disabled={isSubmitting}>
              Отмена
            </button>
          ) : null}
          {onSubmit ? (
            <button type="button" className={styles['submit']} onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Отправка…' : (submitLabel ?? 'Отправить')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
